// src/services/api.ts
import { getConfig } from '../config';
import { toEpochMs } from '../utils/time';
import { ArticleSummary } from '../types/article';
import { articleSummaries } from './articleSummaries';
import { publicIso, publicUrl } from './publicRecords';
import { fetchWorks } from './work';

// The BASE_URL will now be retrieved from the loaded configuration.
// const BASE_URL = 'https://muu-e9991c8895.heteml.net/room_image';

export interface ImageMetadata {
    characters?: PublicFacet[];
    index_created_at?: number | null;
    facets?: Record<string, PublicFacet>;
    rating?: number | null;
    id: number;
    hash: string;
    time: number | string;
    thumb: string;
    detail_normal: string;
    detail_magnifier: string;
    publish_webp: string;
    published_at: number | string | null;
    room_json: string;
    revised_prompt?: string;
    render_prompt?: string;
    vlm_analyze?: string;
    generation_options: any;
    publish_url: string;
    is_published: number;
    is_discard: number;
    publish_datetime: number | string;
    last_processed_at: number | string;
    publish_version?: number;
    public_title?: string;
    public_description?: string;
    color_palette?: Array<{ hex?: string; bucket?: string; bucket_label?: string; bucket_hex?: string }>;
    color_index?: Array<{ key?: string; label?: string; hex?: string; source_hex?: string }>;
    process_version: string;
    watermark_version: string;
    reviewed_at: number | string;
    reviewed_by: string;
    versions?: any[]; // Assuming versions can be part of the metadata
}

export interface PublicFacet {
    slug: string;
    label: string;
    group_label: string;
}

export interface PublicSearchResult extends ImageContextResult<ImageMetadata> {
    ratings: Array<{ value: number; count: number }>;
    unrated_count: number;
    groups: Record<string, { label: string; count: number; values: Array<{ slug: string; label: string; count: number }> }>;
}

export type IllustrationFacetGroup = 'character' | 'signature' | 'scene' | 'theme';

export interface IllustrationPreviewImage {
    id: number;
    hash: string;
    time: number | string;
    thumb: string;
    published_at: number | string | null;
    public_title?: string;
    index_created_at?: number | null;
}

export type IllustrationBundleImage = IllustrationPreviewImage;

export interface IllustrationBundleGroup {
    label: string;
    count: number;
    values: Array<{ slug: string; label: string; count: number }>;
}

export interface IllustrationBundleBootstrap {
    schema_version: 1;
    as_of: number;
    groups: Record<string, IllustrationBundleGroup>;
    ratings: Array<{ value: number; count: number }>;
    unrated_count: number;
    recent: { items: IllustrationBundleImage[]; total: number };
}

export type IllustrationBundleCondition =
    | { kind: 'recent' }
    | { kind: 'facet'; group: IllustrationFacetGroup; value: string }
    | { kind: 'rating'; value: number }
    | { kind: 'saved'; images: Array<{ id: number; hash: string }> };

export type IllustrationBundleQuery = IllustrationBundleCondition & { request_id: string };
export type IllustrationBundleConditionResult =
    | { request_id: string; items: IllustrationBundleImage[]; total: number }
    | { request_id: string; error: { code: string } };

export interface IllustrationBundleResponse {
    schema_version: 1;
    as_of: number;
    results: IllustrationBundleConditionResult[];
}

export class IllustrationBundleHttpError extends Error {
    constructor(readonly status: number) {
        super('画像プレビューの読み込みに失敗しました。');
        this.name = 'IllustrationBundleHttpError';
    }
}

export async function fetchIllustrationBootstrap(signal?: AbortSignal): Promise<IllustrationBundleBootstrap> {
    const apiHost = getConfig().apiHost.replace(/\/+$/, '');
    const response = await fetch(`${apiHost}/get_index.php?type=illustration_bundle`, { signal, cache: 'no-store' });
    if (!response.ok) throw new IllustrationBundleHttpError(response.status);
    return response.json();
}

export async function fetchIllustrationBundle(queries: IllustrationBundleQuery[], signal?: AbortSignal): Promise<IllustrationBundleResponse> {
    const apiHost = getConfig().apiHost.replace(/\/+$/, '');
    const response = await fetch(`${apiHost}/get_index.php?type=illustration_bundle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schema_version: 1, queries }),
        signal,
    });
    if (!response.ok) throw new IllustrationBundleHttpError(response.status);
    return response.json();
}

export async function fetchPublicSearch(params: URLSearchParams, signal?: AbortSignal): Promise<PublicSearchResult> {
    const query = new URLSearchParams(params);
    query.set('type', 'search');
    const response = await fetch(`${getConfig().apiHost.replace(/\/+$/, '')}/get_index.php?${query}`, { signal });
    if (!response.ok) throw new Error('画像検索の読み込みに失敗しました。');
    return response.json();
}

export async function fetchPopularImages(params = new URLSearchParams(), signal?: AbortSignal): Promise<PublicSearchResult> {
    const query = new URLSearchParams(params);
    query.set('type', 'popular');
    const response = await fetch(`${getConfig().apiHost.replace(/\/+$/, '')}/get_index.php?${query}`, { signal, cache: 'no-store' });
    if (!response.ok) throw new Error('人気画像の読み込みに失敗しました。');
    return response.json();
}

export interface YearData {
    year: string;
    count: number;
    index_json: string;
    cover_thumb: string;
}

export interface RootIndex {
    count: number;
    years: YearData[];
}

export interface MonthData {
    month: string;
    count: number;
    index_json: string;
    cover_thumb: string;
}

export interface YearIndex {
    months: MonthData[];
}

export interface MonthLink {
    year: string;
    month: string;
}

export interface AdjacentMonthLinks {
    newer: MonthLink | null;
    older: MonthLink | null;
}

export interface LatestMonthInfo {
    year: string;
    month: string;
}

export const getIndex = async (): Promise<RootIndex> => {
    const { apiBaseUrl } = getConfig();
    const response = await fetch(`${apiBaseUrl}/index/top`);
    if (!response.ok) {
        throw new Error('Failed to fetch top index');
    }
    return response.json();
};

export const getYearIndex = async (year: string): Promise<YearIndex> => {
    const { apiBaseUrl } = getConfig();
    // Use get_index.php API instead of static index.json
    const response = await fetch(`${apiBaseUrl}/index/year/${year}`);
    if (!response.ok) {
        throw new Error(`Failed to fetch index for ${year}`);
    }
    const data = await response.json();
    // API returns { months, ... }, convert to YearIndex format
    return {
        months: data.months || []
    };
}

export const getLatestMonthInfo = async (): Promise<LatestMonthInfo | null> => {
    const rootIndex = await getIndex();
    const years = rootIndex.years?.map(item => item.year) ?? [];
    if (years.length === 0) {
        return null;
    }

    const latestYear = [...years].sort((a, b) => b.localeCompare(a))[0];
    const yearIndex = await getYearIndex(latestYear);
    const months = yearIndex.months?.map(item => item.month) ?? [];
    if (months.length === 0) {
        return null;
    }

    const latestMonth = [...months].sort((a, b) => b.localeCompare(a))[0];
    return { year: latestYear, month: latestMonth };
};

export const getAdjacentMonthLinks = async (year: string, month: string): Promise<AdjacentMonthLinks> => {
    const rootIndex = await getIndex();
    const allMonthsPromises = rootIndex.years.map(async (y) => {
        const yearIndex = await getYearIndex(y.year);
        return yearIndex.months.map(m => ({ year: y.year, month: m.month }));
    });
    const nestedMonths = await Promise.all(allMonthsPromises);
    const allMonths = nestedMonths.flat().sort((a, b) => {
        const dateA = new Date(parseInt(a.year, 10), parseInt(a.month, 10) - 1);
        const dateB = new Date(parseInt(b.year, 10), parseInt(b.month, 10) - 1);
        return dateB.getTime() - dateA.getTime();
    });
    const currentIndex = allMonths.findIndex(m => m.year === year && m.month === month);
    if (currentIndex === -1) {
        return { newer: null, older: null };
    }
    return {
        newer: currentIndex > 0 ? allMonths[currentIndex - 1] : null,
        older: currentIndex < allMonths.length - 1 ? allMonths[currentIndex + 1] : null,
    };
};

export const getMonthlyIndex = async (year: string, month: string): Promise<{ items: ImageMetadata[] }> => {
    const { apiBaseUrl } = getConfig();
    // Use get_index.php API instead of static index.json
    const response = await fetch(`${apiBaseUrl}/index/month/${year}/${month}`);
    if (!response.ok) {
        // Return empty items for non-existent month instead of throwing
        if (response.status === 404) {
            return { items: [] };
        }
        throw new Error(`Failed to fetch index for ${year}-${month}`);
    }
    const data = await response.json();
    return { items: data.items || [] };
};

export const getMetadata = async (imagePath: string): Promise<ImageMetadata> => {
    const { apiBaseUrl } = getConfig();
    const response = await fetch(`${apiBaseUrl}/${imagePath}`);
    if (!response.ok) {
        throw new Error(`Failed to fetch metadata for ${imagePath}`);
    }
    return response.json();
};

export const fetchMonthImages = async (year: string, month: string): Promise<ImageMetadata[]> => {
    const monthlyIndex = await getMonthlyIndex(year, month);
    return monthlyIndex.items.sort((a: ImageMetadata, b: ImageMetadata) => {
        const aTime = toEpochMs(a.time) ?? 0;
        const bTime = toEpochMs(b.time) ?? 0;
        return bTime - aTime;
    });
};

export const fetchDiscardedImages = async (): Promise<ImageMetadata[]> => {
    const { apiBaseUrl } = getConfig();
    const response = await fetch(`${apiBaseUrl}/discarded_index.json`);
    if (!response.ok) {
        throw new Error('Failed to fetch discarded_index.json');
    }
    const data = await response.json();
    return data.items.sort((a: ImageMetadata, b: ImageMetadata) => {
        const aTime = toEpochMs(a.time) ?? 0;
        const bTime = toEpochMs(b.time) ?? 0;
        return bTime - aTime;
    });
}

export const fetchAllImages = async (): Promise<ImageMetadata[]> => {
    const rootIndex = await getIndex();
    const allImages: ImageMetadata[] = [];

    if (rootIndex.years && Array.isArray(rootIndex.years)) {
        for (const yearData of rootIndex.years) {
            const year = yearData.year;
            try {
                const yearIndex = await getYearIndex(year);
                if (yearIndex.months && Array.isArray(yearIndex.months)) {
                    for (const monthData of yearIndex.months) {
                        const month = monthData.month;
                        try {
                            const monthlyIndex = await getMonthlyIndex(year, month);
                            if (monthlyIndex.items) {
                                allImages.push(...monthlyIndex.items);
                            }
                        } catch (error) {
                            console.error(`Could not fetch monthly index for ${year}-${month}:`, error);
                        }
                    }
                }
            } catch (error) {
                console.error(`Could not fetch year index for ${year}:`, error);
            }
        }
    }

    return allImages.sort((a: ImageMetadata, b: ImageMetadata) => {
        const aTime = toEpochMs(a.time) ?? 0;
        const bTime = toEpochMs(b.time) ?? 0;
        return bTime - aTime;
    });
};

export interface Song {
    id: string;
    title: string;
    platform: string;
    embedUrl: string;
    songUrl: string;
    thumbnailUrl: string;
    description: string;
    publishedAt: number | string;
    type: string;
    extensionContent?: string;
}

export const fetchSongs = async (): Promise<Song[]> => {
    return (await fetchWorks()).map(work => ({ id: work.id, title: work.title, platform: work.platform,
        embedUrl: work.playback.embedUrl || '', songUrl: work.links[0]?.url || '', thumbnailUrl: work.thumbnail || '',
        description: work.description, publishedAt: work.publishedAt || '', type: work.sourceType,
        extensionContent: work.playback.mediaUrl || '' }));
};

export const fetchArticleIndex = async () => {
    const response = await fetch('/articles/index.json');
    if (!response.ok) {
        throw new Error('Failed to fetch articles index.json');
    }
    return response.json();
};

export const fetchArticleDetail = async (year: string, month: string, id: string) => {
    const response = await fetch(`/articles/${year}/${month}/${id}.json`);
    if (!response.ok) {
        throw new Error(`Failed to fetch article ${year}/${month}/${id}`);
    }
    return response.json();
};

/**
 * Fetch server time to sync with publishing schedule.
 */
export const fetchServerTime = async (): Promise<number> => {
    const { apiHost } = getConfig();
    try {
        const response = await fetch(`${apiHost}/now`);
        if (response.ok) {
            const data = await response.json();
            return data.now;
        }
    } catch (error) {
        console.warn('Failed to fetch server time, falling back to client time:', error);
    }
    return Date.now();
};

/**
 * Fetch the latest N images from the API.
 * Returns images sorted by published_at (newest first).
 */
export const fetchLatestImages = async (limit: number = 6): Promise<ImageMetadata[]> => {
    try {
        const { apiBaseUrl } = getConfig();
        const response = await fetch(`${apiBaseUrl}/index/latest?limit=${limit}`);
        if (!response.ok) {
            throw new Error(`Failed to fetch latest images: ${response.status}`);
        }
        const data = await response.json();
        if (data.items && Array.isArray(data.items)) {
            return data.items.slice(0, limit);
        }
    } catch (error) {
        console.warn('Failed to fetch latest images:', error);
    }
    return [];
};

export interface PaginatedResult<T> {
    items: T[];
    pagination: {
        current_page: number;
        per_page: number;
        total_items: number;
        total_pages: number;
    };
    [key: string]: any;
}

export interface ImageContextResult<T> extends PaginatedResult<T> {
    context?: {
        target_index: number;
        target_page: number;
        target_index_in_page: number;
        target_item: T;
        prev: T | null;
        next: T | null;
    };
}

/**
 * Fetch paginated images using the get_index.php API (via rewritten URLs).
 */
export const fetchPaginatedImages = async (
    type: 'month' | 'year' | 'word' | 'color' | 'top',
    params: { y?: string; m?: string; w?: string; c?: string; page?: number; per_page?: number }
): Promise<PaginatedResult<ImageMetadata>> => {
    const { apiBaseUrl } = getConfig();

    // Construct URL based on rewritten patterns in .htaccess
    let url = `${apiBaseUrl}/index/${type}`;
    if (type === 'month' && params.y && params.m) {
        url += `/${params.y}/${params.m}`;
    } else if (type === 'year' && params.y) {
        url += `/${params.y}`;
    }

    const query = new URLSearchParams();
    if (type === 'word' && params.w) query.append('w', params.w);
    if (type === 'color' && params.c) query.append('c', params.c);
    if (params.page) query.append('page', params.page.toString());
    if (params.per_page) query.append('per_page', params.per_page.toString());

    const queryString = query.toString();
    if (queryString) {
        url += `?${queryString}`;
    }

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch paginated index: ${response.status} ${response.statusText}`);
    }
    return response.json();
};

/**
 * Fetch paginated images with target context (target item + prev/next).
 */
export const fetchImageContext = async (
    type: 'month' | 'word' | 'color',
    params: { y?: string; m?: string; w?: string; c?: string; per_page?: number },
    target: { id?: number; hash?: string }
): Promise<ImageContextResult<ImageMetadata>> => {
    const { apiBaseUrl } = getConfig();

    let url = `${apiBaseUrl}/index/${type}`;
    if (type === 'month' && params.y && params.m) {
        url += `/${params.y}/${params.m}`;
    }

    const query = new URLSearchParams();
    if (type === 'word' && params.w) query.append('w', params.w);
    if (type === 'color' && params.c) query.append('c', params.c);
    if (params.per_page) query.append('per_page', params.per_page.toString());
    if (target.id) query.append('id', target.id.toString());
    if (target.hash) query.append('hash', target.hash);

    const queryString = query.toString();
    if (queryString) {
        url += `?${queryString}`;
    }

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch image context: ${response.status} ${response.statusText}`);
    }
    return response.json();
};

/**
 * Fetch the latest N works/songs.
 * Returns works sorted by publishedAt (newest first).
 */
export const fetchLatestWorks = async (limit: number = 3): Promise<Song[]> => {
    const songs = await fetchSongs();
    // Sort by publishedAt descending
    const sorted = songs.sort((a, b) => {
        const aTime = toEpochMs(a.publishedAt) ?? 0;
        const bTime = toEpochMs(b.publishedAt) ?? 0;
        return bTime - aTime;
    });
    return sorted.slice(0, limit);
};

/**
 * Fetch the latest N articles with details.
 * Returns articles sorted by published_at (newest first).
 */
export type ArticleSummaryForLanding = ArticleSummary;

export class ArticleSummaryLoadError extends Error {
    constructor(public readonly articles: ArticleSummary[]) {
        super('Failed to load some article summaries');
        this.name = 'ArticleSummaryLoadError';
    }
}

export const fetchArticleSummaries = async (limit?: number): Promise<ArticleSummary[]> => {
    const summaries = articleSummaries(await fetchArticleIndex());
    const selected = limit === undefined ? summaries : summaries.slice(0, limit);
    const result: Array<ArticleSummary | undefined> = new Array(selected.length);
    let next = 0, failed = false;
    // Bound concurrent legacy requests, not the number of articles we can display.
    await Promise.all(Array.from({ length: Math.min(4, selected.length) }, async () => {
        while (next < selected.length) {
            const index = next++, article = selected[index];
            if (article.schemaVersion === 2) { result[index] = article; continue; }
            try {
                const detail = await fetchArticleDetail(article.year, article.month, article.id);
                if (!detail || typeof detail !== 'object' || Array.isArray(detail) || detail.article_id !== article.id) throw new Error('Invalid article detail');
                if (detail.status && !['public', 'published'].includes(detail.status)) continue;
                const stamp = detail.published_at ?? article.publishedAt;
                const publishedAt = publicIso(stamp);
                if (stamp != null && !publishedAt) throw new Error('Invalid article publication date');
                if (publishedAt && Date.parse(publishedAt) > Date.now()) continue;
                if (['title', 'excerpt', 'cover_image'].some(key => detail[key] != null && typeof detail[key] !== 'string')) throw new Error('Invalid article fields');
                const coverImage = publicUrl(detail.cover_image) || undefined;
                if (detail.cover_image?.trim() && !coverImage) throw new Error('Invalid article cover');
                result[index] = { ...article, title: typeof detail.title === 'string' ? detail.title : undefined,
                    excerpt: typeof detail.excerpt === 'string' ? detail.excerpt : article.excerpt,
                    coverImage, publishedAt };
            } catch { failed = true; }
        }
    }));
    const articles = result.filter((article): article is ArticleSummary => article !== undefined);
    if (failed) throw new ArticleSummaryLoadError(articles);
    return articles;
};

export const fetchLatestArticles = (limit: number = 3): Promise<ArticleSummary[]> => fetchArticleSummaries(limit);

/**
 * Fetch image scores from score_api.php
 */
export const fetchImageScores = async (): Promise<Record<string, number>> => {
    const { apiHost } = getConfig();
    try {
        const response = await fetch(`${apiHost}/score_api.php`);
        if (!response.ok) {
            console.warn('Failed to fetch scores, returning empty object');
            return {};
        }
        return response.json();
    } catch (error) {
        console.warn('Error fetching scores:', error);
        return {};
    }
};

/**
 * Fetch image URL from score_api.php by ID
 */
export const fetchScoreUrl = async (id: string): Promise<string | null> => {
    const { apiHost } = getConfig();
    try {
        const response = await fetch(`${apiHost}/score_api.php?id=${id}`);
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        return data.url || null;
    } catch (error) {
        console.warn(`Error fetching score URL for id ${id}:`, error);
        return null;
    }
};

/**
 * Fetch top N scored images from score_api.php
 * Returns array of { id, score, url }
 */
export interface TopScoredImage {
    id: string;
    score: number;
    url: string;
}

export const fetchTopScoredImages = async (limit: number = 30): Promise<TopScoredImage[]> => {
    const { apiHost } = getConfig();
    try {
        const response = await fetch(`${apiHost}/score_api.php?top=${limit}`);
        if (!response.ok) {
            console.warn('Failed to fetch top scored images');
            return [];
        }
        return response.json();
    } catch (error) {
        console.warn('Error fetching top scored images:', error);
        return [];
    }
};
